import type { Prisma } from '@prisma/client';
import { z } from 'zod';
import {
  registerImportEntity,
  type ImportColumn,
  type ImportContext,
  type ImportEntity,
  type ParsedRow,
} from '../csv/csv.registry.js';
import {
  contactSchema,
  createParty,
  partyInclude,
  partyInputSchema,
  updateParty,
  type ContactInput,
  type PartyInput,
} from './parties.service.js';

const truthy = new Set(['yes', 'y', 'true', '1', 'x']);
const falsy = new Set(['no', 'n', 'false', '0', '']);

export function parseBool(v: string | undefined, label: string, errors: string[]): boolean {
  const t = (v ?? '').trim().toLowerCase();
  if (truthy.has(t)) return true;
  if (falsy.has(t)) return false;
  errors.push(`${label}: use yes or no`);
  return false;
}

export function issuesToErrors(error: z.ZodError, labels: Record<string, string> = {}): string[] {
  return error.issues.map((i) => {
    const key = String(i.path[0] ?? '');
    const where = labels[key] ?? (key ? key : 'Row');
    return `${where}: ${i.message}`;
  });
}

function parseIntField(
  v: string | undefined,
  label: string,
  fallback: number,
  errors: string[],
): number {
  if (!v || v.trim() === '') return fallback;
  if (!/^\d{1,4}$/.test(v.trim())) {
    errors.push(`${label}: enter a whole number`);
    return fallback;
  }
  return Number(v.trim());
}

interface PartyRowValue {
  input: PartyInput;
  ownerEmail: string | null;
}

const partyColumns = (vendor: boolean): ImportColumn[] => [
  {
    key: 'name',
    label: 'Name',
    required: true,
    example: vendor ? 'Dell East Africa Ltd' : 'Acme Uganda Ltd',
    aliases: ['company', 'client name', 'vendor name', 'customer'],
  },
  { key: 'type', label: 'Type (company/individual)', example: 'company' },
  ...(vendor
    ? []
    : [{ key: 'kind', label: 'Kind (client/prospect)', example: 'client' } as ImportColumn]),
  { key: 'tin', label: 'TIN', example: '1000123456' },
  { key: 'vat_registered', label: 'VAT registered', example: 'yes', aliases: ['vat'] },
  { key: 'vat_number', label: 'VAT number', example: '' },
  { key: 'contact_name', label: 'Contact person', example: 'Jane Nakato', aliases: ['contact'] },
  { key: 'email', label: 'Email', example: 'billing@acme.co.ug' },
  { key: 'phone', label: 'Phone', example: '0772123456', aliases: ['telephone', 'mobile'] },
  { key: 'whatsapp', label: 'WhatsApp', example: '0772123456' },
  { key: 'address', label: 'Address', example: 'Plot 5, Kampala Road' },
  { key: 'city', label: 'City', example: 'Kampala' },
  { key: 'country', label: 'Country', example: 'Uganda' },
  { key: 'website', label: 'Website', example: 'https://acme.co.ug' },
  { key: 'industry', label: 'Industry', example: 'Retail' },
  { key: 'currency', label: 'Currency', example: 'UGX' },
  { key: 'payment_terms_days', label: 'Payment terms (days)', example: '30' },
  ...(vendor
    ? [
        { key: 'wht_applicable', label: 'WHT applicable', example: 'no' } as ImportColumn,
        {
          key: 'payment_details',
          label: 'Payment details',
          example: 'Stanbic 9030001234',
        } as ImportColumn,
      ]
    : [
        { key: 'credit_limit', label: 'Credit limit', example: '5000000' } as ImportColumn,
        { key: 'wht_agent', label: 'WHT agent', example: 'no' } as ImportColumn,
      ]),
  { key: 'tags', label: 'Tags (; separated)', example: 'hosting;retail' },
  { key: 'notes', label: 'Notes', example: '' },
  { key: 'owner_email', label: 'Owner email', example: 'sales@roovers.local' },
];

function partySnapshot(p: Prisma.PartyGetPayload<{ include: typeof partyInclude }>) {
  const { contacts, tags, ...columns } = p;
  return {
    columns: JSON.parse(JSON.stringify(columns)),
    contactIds: contacts.map((c) => c.id),
    tags: tags.map((t) => t.tag.name),
  };
}

function makePartyEntity(kind: 'clients' | 'vendors'): ImportEntity<PartyRowValue> {
  const vendor = kind === 'vendors';
  const columns = partyColumns(vendor);
  const labels = Object.fromEntries(columns.map((c) => [c.key, c.label]));
  return {
    key: kind,
    label: vendor ? 'Vendors' : 'Clients & Prospects',
    importPermission: vendor ? 'purchases.vendor.import' : 'sales.client.import',
    columns,
    parseRow(raw): ParsedRow<PartyRowValue> {
      const errors: string[] = [];
      const clientKind = (raw.kind ?? '').trim().toLowerCase() || 'client';
      if (!vendor && !['client', 'prospect'].includes(clientKind))
        errors.push('Kind: use client or prospect');
      const contactName =
        raw.contact_name?.trim() || (raw.email || raw.phone || raw.whatsapp ? raw.name : '');
      const contacts: Partial<ContactInput>[] = contactName
        ? [
            {
              name: contactName,
              email: raw.email || null,
              phone: raw.phone || null,
              whatsappNumber: raw.whatsapp || null,
              receivesDocuments: !!raw.email,
              isPrimary: true,
            },
          ]
        : [];
      const rawInput = {
        name: raw.name,
        type: raw.type?.trim().toLowerCase() || 'company',
        isClient: !vendor && clientKind === 'client',
        isProspect: !vendor && clientKind === 'prospect',
        isVendor: vendor,
        tin: raw.tin || null,
        vatRegistered: parseBool(raw.vat_registered, 'VAT registered', errors),
        vatNumber: raw.vat_number || null,
        address: raw.address || null,
        city: raw.city || null,
        country: raw.country?.trim() || 'Uganda',
        website: raw.website || null,
        industry: raw.industry || null,
        currency: raw.currency?.trim() || 'UGX',
        paymentTermsDays: parseIntField(raw.payment_terms_days, 'Payment terms', 30, errors),
        creditLimit: raw.credit_limit ?? '',
        whtAgent: vendor ? false : parseBool(raw.wht_agent, 'WHT agent', errors),
        whtApplicable: vendor ? parseBool(raw.wht_applicable, 'WHT applicable', errors) : false,
        paymentDetails: raw.payment_details || null,
        notes: raw.notes || null,
        tags: (raw.tags ?? '')
          .split(';')
          .map((t) => t.trim())
          .filter(Boolean),
        contacts,
      };
      const parsed = partyInputSchema.safeParse(rawInput);
      if (!parsed.success) {
        const kindAlreadyReported = errors.some((e) => e.startsWith('Kind:'));
        errors.push(
          ...issuesToErrors(parsed.error, {
            ...labels,
            isClient: 'Kind',
            contacts: 'Contact',
          }).filter((m) => !(kindAlreadyReported && m.startsWith('Kind:'))),
        );
      }
      if (errors.length || !parsed.success) return { ok: false, errors };
      return {
        ok: true,
        value: { input: parsed.data, ownerEmail: raw.owner_email?.trim().toLowerCase() || null },
      };
    },
    rowKey: (v) =>
      v.input.tin
        ? `tin:${v.input.tin.toLowerCase()}`
        : v.input.contacts[0]?.email
          ? `email:${v.input.contacts[0].email}`
          : `name:${v.input.name.toLowerCase()}`,
    async validate(ctx, v) {
      if (!v.ownerEmail) return [];
      const owner = await ctx.tx.user.findFirst({
        where: { companyId: ctx.companyId, email: v.ownerEmail, status: 'active' },
      });
      return owner ? [] : [`Owner email: no active user "${v.ownerEmail}"`];
    },
    async findExisting(ctx, v) {
      let existing = v.input.tin
        ? await ctx.tx.party.findFirst({
            where: { companyId: ctx.companyId, tin: v.input.tin },
            include: partyInclude,
          })
        : null;
      const email = v.input.contacts[0]?.email;
      if (!existing && email) {
        const c = await ctx.tx.partyContact.findFirst({
          where: { email, party: { companyId: ctx.companyId } },
        });
        if (c)
          existing = await ctx.tx.party.findUnique({
            where: { id: c.partyId },
            include: partyInclude,
          });
      }
      return existing ? { id: existing.id, snapshot: partySnapshot(existing) } : null;
    },
    async create(ctx, v) {
      const ownerId = v.ownerEmail
        ? (
            await ctx.tx.user.findFirst({
              where: { companyId: ctx.companyId, email: v.ownerEmail },
            })
          )?.id
        : undefined;
      const party = await createParty(ctx.tx, {
        companyId: ctx.companyId,
        userId: ctx.userId,
        input: { ...v.input, ownerId },
      });
      return { id: party.id };
    },
    async update(ctx, id, v) {
      const current = await ctx.tx.party.findUniqueOrThrow({
        where: { id },
        include: partyInclude,
      });
      const { contacts, tags, name, ...rest } = v.input;
      const patch: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(rest)) {
        if (val !== null && val !== undefined && val !== '' && !(typeof val === 'boolean' && !val))
          patch[k] = val;
      }
      // Existing contacts are never rewritten (their ids must survive so a rollback can restore the
      // record); only contacts with an email we do not already have are added.
      const known = new Set(current.contacts.map((c) => c.email).filter(Boolean));
      const fresh = contacts.filter((c) => !c.email || !known.has(c.email));
      await updateParty(ctx.tx, {
        companyId: ctx.companyId,
        userId: ctx.userId,
        id,
        input: {
          ...patch,
          name,
          tags: [...new Set([...current.tags.map((t) => t.tag.name), ...tags])],
        } as never,
      });
      for (const c of fresh) {
        const { id: _id, ...data } = c;
        await ctx.tx.partyContact.create({
          data: { ...data, isPrimary: current.contacts.length === 0 && c.isPrimary, partyId: id },
        });
      }
    },
    async restore(ctx, id, before) {
      const snap = before as ReturnType<typeof partySnapshot>;
      const {
        id: _id,
        companyId: _c,
        createdAt: _ca,
        updatedAt: _ua,
        customFields,
        creditLimit,
        ...cols
      } = snap.columns as Record<string, unknown>;
      await ctx.tx.party.update({
        where: { id },
        data: {
          ...(cols as Prisma.PartyUpdateInput),
          creditLimit: creditLimit as string | null,
          customFields: (customFields ?? undefined) as Prisma.InputJsonValue | undefined,
        },
      });
      await ctx.tx.partyContact.deleteMany({
        where: { partyId: id, id: { notIn: snap.contactIds } },
      });
      const tagRows = await ctx.tx.tag.findMany({
        where: { companyId: ctx.companyId, name: { in: snap.tags } },
      });
      await ctx.tx.partyTag.deleteMany({ where: { partyId: id } });
      await ctx.tx.partyTag.createMany({
        data: tagRows.map((t) => ({ partyId: id, tagId: t.id })),
      });
    },
    async remove(ctx, id) {
      try {
        await ctx.tx.party.delete({ where: { id } });
        return null;
      } catch {
        return 'this record is now used by other documents and cannot be removed';
      }
    },
  };
}

// ───────── contacts (demonstrates missing-reference handling) ─────────

interface ContactRowValue {
  partyName: string;
  contact: ContactInput;
}

async function partyIdByName(ctx: ImportContext, name: string): Promise<number | null> {
  const found = await ctx.tx.party.findFirst({
    where: { companyId: ctx.companyId, name },
    select: { id: true },
  });
  return found?.id ?? null;
}

const contactEntity: ImportEntity<ContactRowValue> = {
  key: 'contacts',
  label: 'Contact persons',
  importPermission: 'sales.client.import',
  columns: [
    {
      key: 'party_name',
      label: 'Client or vendor name',
      required: true,
      example: 'Acme Uganda Ltd',
      aliases: ['client', 'vendor', 'company'],
    },
    { key: 'name', label: 'Contact name', required: true, example: 'Jane Nakato' },
    { key: 'position', label: 'Position', example: 'Finance Manager' },
    { key: 'email', label: 'Email', example: 'jane@acme.co.ug' },
    { key: 'phone', label: 'Phone', example: '0772123456' },
    { key: 'whatsapp', label: 'WhatsApp', example: '0772123456' },
    { key: 'receives_documents', label: 'Receives documents', example: 'yes' },
    { key: 'primary', label: 'Primary contact', example: 'no' },
  ],
  parseRow(raw) {
    const errors: string[] = [];
    if (!raw.party_name?.trim()) errors.push('Client or vendor name: required');
    const parsed = contactSchema.safeParse({
      name: raw.name,
      position: raw.position || null,
      email: raw.email ?? '',
      phone: raw.phone || null,
      whatsappNumber: raw.whatsapp || null,
      receivesDocuments: parseBool(raw.receives_documents, 'Receives documents', errors),
      isPrimary: parseBool(raw.primary, 'Primary contact', errors),
    });
    if (!parsed.success)
      errors.push(
        ...issuesToErrors(parsed.error, {
          name: 'Contact name',
          email: 'Email',
          phone: 'Phone',
          whatsappNumber: 'WhatsApp',
        }),
      );
    if (errors.length || !parsed.success) return { ok: false, errors };
    return { ok: true, value: { partyName: raw.party_name!.trim(), contact: parsed.data } };
  },
  rowKey: (v) =>
    `${v.partyName.toLowerCase()}|${(v.contact.email ?? v.contact.name).toLowerCase()}`,
  async findExisting(ctx, v) {
    const partyId = await partyIdByName(ctx, v.partyName);
    if (!partyId) return null;
    const c = await ctx.tx.partyContact.findFirst({
      where: {
        partyId,
        ...(v.contact.email ? { email: v.contact.email } : { name: v.contact.name }),
      },
    });
    return c ? { id: c.id, snapshot: c } : null;
  },
  async missingRefs(ctx, v) {
    return (await partyIdByName(ctx, v.partyName)) ? [] : [{ kind: 'client', value: v.partyName }];
  },
  /** "Create automatically": an Incomplete client built from the name in the file. */
  async createRef(ctx, ref) {
    const party = await createParty(ctx.tx, {
      companyId: ctx.companyId,
      userId: ctx.userId,
      needsReview: true,
      input: partyInputSchema.parse({ name: ref.value, isClient: true }),
    });
    return party.id;
  },
  async removeRef(ctx, ref) {
    try {
      await ctx.tx.party.delete({ where: { id: ref.id } });
      return null;
    } catch {
      return 'a client created by this import is now used by other documents';
    }
  },
  async create(ctx, v, refs) {
    const partyId = (await partyIdByName(ctx, v.partyName)) ?? refs.get(`client:${v.partyName}`);
    if (!partyId) throw new Error(`Client "${v.partyName}" could not be resolved`);
    const { id: _id, ...data } = v.contact;
    const created = await ctx.tx.partyContact.create({ data: { ...data, partyId } });
    return { id: created.id };
  },
  async update(ctx, id, v) {
    const { id: _id, ...data } = v.contact;
    await ctx.tx.partyContact.update({ where: { id }, data });
  },
  async restore(ctx, id, before) {
    const { id: _id, partyId: _p, ...data } = before as Record<string, unknown>;
    await ctx.tx.partyContact.update({
      where: { id },
      data: data as Prisma.PartyContactUpdateInput,
    });
  },
  async remove(ctx, id) {
    await ctx.tx.partyContact.delete({ where: { id } });
    return null;
  },
};

registerImportEntity(makePartyEntity('clients'));
registerImportEntity(makePartyEntity('vendors'));
registerImportEntity(contactEntity);
